import type { CandidateFileDataScanStatus } from '@jobilla/nexus-sdk'
import {
	ClockIcon,
	LoaderCircleIcon,
	type LucideIcon,
	ShieldAlertIcon,
	ShieldCheckIcon,
	ShieldOffIcon,
	TriangleAlertIcon,
} from 'lucide-react'

export type ScanStatus = CandidateFileDataScanStatus | null

export type ResolvedScanState =
	| 'clean'
	| 'scanning'
	| 'queued'
	| 'pending'
	| 'infected'
	| 'error'
	| 'skipped'

export interface ScanPresentation {
	state: ResolvedScanState
	labelKey: string
	Icon: LucideIcon
	spin: boolean
	badgeClass: string
	tagClass: string
}

const PRESENTATIONS: Record<ResolvedScanState, Omit<ScanPresentation, 'state'>> = {
	clean: {
		labelKey: 'fileScan.clean',
		Icon: ShieldCheckIcon,
		spin: false,
		badgeClass: 'bg-green-600 text-white',
		tagClass: 'text-green-700',
	},
	scanning: {
		labelKey: 'fileScan.scanning',
		Icon: LoaderCircleIcon,
		spin: true,
		badgeClass: 'bg-blue-500 text-white',
		tagClass: 'text-blue-600',
	},
	queued: {
		labelKey: 'fileScan.queued',
		Icon: ClockIcon,
		spin: false,
		badgeClass: 'bg-gray-400 text-white',
		tagClass: 'text-gray-500',
	},
	pending: {
		labelKey: 'fileScan.pending',
		Icon: ClockIcon,
		spin: false,
		badgeClass: 'bg-gray-400 text-white',
		tagClass: 'text-gray-500',
	},
	infected: {
		labelKey: 'fileScan.infected',
		Icon: ShieldAlertIcon,
		spin: false,
		badgeClass: 'bg-red-600 text-white',
		tagClass: 'text-red-700',
	},
	error: {
		labelKey: 'fileScan.error',
		Icon: TriangleAlertIcon,
		spin: false,
		badgeClass: 'bg-amber-500 text-white',
		tagClass: 'text-amber-700',
	},
	skipped: {
		labelKey: 'fileScan.skipped',
		Icon: ShieldOffIcon,
		spin: false,
		badgeClass: 'bg-gray-300 text-gray-600',
		tagClass: 'text-gray-400',
	},
}

export function resolveScanState(status: ScanStatus): ResolvedScanState {
	return status ?? 'pending'
}

export function getScanPresentation(status: ScanStatus): ScanPresentation {
	const state = resolveScanState(status)
	return { state, ...PRESENTATIONS[state] }
}

export function isScanBlocked(status: ScanStatus): boolean {
	return status === 'infected'
}

export function isScanPending(status: ScanStatus): boolean {
	const state = resolveScanState(status)
	return state === 'scanning' || state === 'queued' || state === 'pending'
}

export function canDownloadScannedFile(status: ScanStatus): boolean {
	return !isScanBlocked(status) && !isScanPending(status)
}
